import { useState, useEffect } from 'react';
import {
  Workflow,
  Plus,
  Search,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Play,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  X,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Download,
  ExternalLink,
  Settings,
  History,
  Link2,
  Copy,
  Check,
  Eye,
} from 'lucide-react';
import { Button, Input, useToast } from '@centraoneplus/ui';
import n8nService, {
  N8nWorkflow,
  N8nExecution,
  N8nAvailableWorkflow,
  N8nTriggerType,
  N8nExecutionStatus,
  CreateN8nWorkflowDto,
  UpdateN8nWorkflowDto,
} from '@/services/n8n.service';

// Trigger type labels - must match N8nTriggerType enum
const TRIGGER_LABELS: Record<N8nTriggerType, string> = {
  [N8nTriggerType.TICKET_CREATED]: 'Ticket Created',
  [N8nTriggerType.TICKET_UPDATED]: 'Ticket Updated',
  [N8nTriggerType.TICKET_STATUS_CHANGED]: 'Status Changed',
  [N8nTriggerType.TICKET_ASSIGNED]: 'Ticket Assigned',
  [N8nTriggerType.TICKET_PRIORITY_CHANGED]: 'Priority Changed',
  [N8nTriggerType.TICKET_TAG_ADDED]: 'Tag Added',
  [N8nTriggerType.STAGE_ENTERED]: 'Stage Entered',
  [N8nTriggerType.STAGE_EXITED]: 'Stage Exited',
  [N8nTriggerType.CATEGORY_CHANGED]: 'Category Changed',
  [N8nTriggerType.FIELD_VALUE_CHANGED]: 'Field Value Changed',
  [N8nTriggerType.MESSAGE_RECEIVED]: 'Message Received',
  [N8nTriggerType.MESSAGE_SENT]: 'Message Sent',
  [N8nTriggerType.CHAT_STARTED]: 'Chat Started',
  [N8nTriggerType.CHAT_ENDED]: 'Chat Ended',
  [N8nTriggerType.CHAT_AGENT_ASSIGNED]: 'Chat Agent Assigned',
  [N8nTriggerType.CUSTOMER_CREATED]: 'Customer Created',
  [N8nTriggerType.CUSTOMER_UPDATED]: 'Customer Updated',
  [N8nTriggerType.SLA_BREACH_WARNING]: 'SLA Breach Warning',
  [N8nTriggerType.SLA_BREACHED]: 'SLA Breached',
  [N8nTriggerType.TIME_BASED]: 'Time Based (Scheduled)',
  [N8nTriggerType.WEBHOOK_RECEIVED]: 'Webhook Received',
};

const STATUS_COLORS: Record<N8nExecutionStatus, string> = {
  [N8nExecutionStatus.PENDING]: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  [N8nExecutionStatus.RUNNING]: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  [N8nExecutionStatus.COMPLETED]: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  [N8nExecutionStatus.FAILED]: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  [N8nExecutionStatus.SKIPPED]: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
};

const STATUS_ICONS: Record<N8nExecutionStatus, React.ReactNode> = {
  [N8nExecutionStatus.PENDING]: <Clock className="w-3 h-3" />,
  [N8nExecutionStatus.RUNNING]: <Loader2 className="w-3 h-3 animate-spin" />,
  [N8nExecutionStatus.COMPLETED]: <CheckCircle className="w-3 h-3" />,
  [N8nExecutionStatus.FAILED]: <XCircle className="w-3 h-3" />,
  [N8nExecutionStatus.SKIPPED]: <AlertCircle className="w-3 h-3" />,
};

function getErrorMessage(error: any): string {
  const data = error?.response?.data;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(', ') : data.message;
  }
  return error?.message || 'An unexpected error occurred';
}

export function N8nSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'workflows' | 'executions' | 'connection'>('workflows');
  const [workflows, setWorkflows] = useState<N8nWorkflow[]>([]);
  const [executions, setExecutions] = useState<N8nExecution[]>([]);
  const [availableWorkflows, setAvailableWorkflows] = useState<N8nAvailableWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<N8nWorkflow | null>(null);
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<string>>(new Set());
  const [expandedExecutions, setExpandedExecutions] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [triggeringId, setTriggeringId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Connection state
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);

  // Form state for workflow
  const [formData, setFormData] = useState<CreateN8nWorkflowDto>({
    name: '',
    description: '',
    n8nWorkflowId: '',
    webhookUrl: '',
    triggerType: N8nTriggerType.WEBHOOK_RECEIVED,
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'workflows') {
        const response = await n8nService.getWorkflows({ limit: 50 });
        setWorkflows(response.data || []);
      } else if (activeTab === 'executions') {
        const response = await n8nService.getExecutions({ limit: 50 });
        setExecutions(response.data || []);
      } else if (activeTab === 'connection') {
        await testConnection();
      }
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const result = await n8nService.testConnection();
      setConnectionStatus(result.success ? 'connected' : 'error');
      setConnectionMessage(
        result.success
          ? `Connected successfully. ${result.workflowCount} workflows available.`
          : result.message
      );
      if (result.success) {
        const available = await n8nService.getAvailableWorkflows();
        setAvailableWorkflows(available);
      }
    } catch (error: any) {
      setConnectionStatus('error');
      setConnectionMessage(getErrorMessage(error));
    } finally {
      setTestingConnection(false);
    }
  };

  const loadAvailableWorkflows = async () => {
    try {
      const available = await n8nService.getAvailableWorkflows();
      setAvailableWorkflows(available);
      return available;
    } catch (error: any) {
      toast.error('Error', 'Failed to load workflows from n8n: ' + getErrorMessage(error));
      return [];
    }
  };

  const toggleExpand = (id: string, type: 'workflow' | 'execution') => {
    if (type === 'workflow') {
      const newExpanded = new Set(expandedWorkflows);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      setExpandedWorkflows(newExpanded);
    } else {
      const newExpanded = new Set(expandedExecutions);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      setExpandedExecutions(newExpanded);
    }
  };

  const handleToggleWorkflow = async (workflow: N8nWorkflow) => {
    try {
      await n8nService.updateWorkflow(workflow.id, { isActive: !workflow.isActive });
      toast.success('Success', `Workflow ${workflow.isActive ? 'disabled' : 'enabled'}`);
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  const handleDeleteWorkflow = async (workflow: N8nWorkflow) => {
    if (!confirm(`Are you sure you want to delete "${workflow.name}"?`)) {
      return;
    }
    try {
      await n8nService.deleteWorkflow(workflow.id);
      toast.success('Success', 'Workflow deleted');
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  const handleTriggerWorkflow = async (workflow: N8nWorkflow) => {
    try {
      setTriggeringId(workflow.id);
      const result = await n8nService.triggerWorkflow(workflow.id, {
        testTrigger: true,
        timestamp: new Date().toISOString(),
      });
      if (result.success) {
        toast.success('Success', `Workflow triggered. Execution ID: ${result.executionId?.slice(0, 8)}...`);
      } else {
        toast.error('Error', result.error || 'Failed to trigger workflow');
      }
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setTriggeringId(null);
    }
  };

  const handleCreateWorkflow = async () => {
    if (!formData.name || !formData.n8nWorkflowId) {
      toast.error('Validation Error', 'Name and n8n Workflow ID are required');
      return;
    }

    try {
      setSaving(true);
      await n8nService.createWorkflow(formData);
      toast.success('Success', 'Workflow mapping created');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWorkflow = async () => {
    if (!selectedWorkflow) return;

    try {
      setSaving(true);
      await n8nService.updateWorkflow(selectedWorkflow.id, formData as UpdateN8nWorkflowDto);
      toast.success('Success', 'Workflow mapping updated');
      setShowEditModal(false);
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleImportWorkflow = async (n8nWorkflow: N8nAvailableWorkflow) => {
    setFormData({
      name: n8nWorkflow.name,
      description: `Imported from n8n: ${n8nWorkflow.name}`,
      n8nWorkflowId: n8nWorkflow.id,
      webhookUrl: '',
      triggerType: N8nTriggerType.WEBHOOK_RECEIVED,
      isActive: n8nWorkflow.active,
    });
    setShowImportModal(false);
    setShowCreateModal(true);
  };

  const openEditModal = (workflow: N8nWorkflow) => {
    setSelectedWorkflow(workflow);
    setFormData({
      name: workflow.name,
      description: workflow.description || '',
      n8nWorkflowId: workflow.n8nWorkflowId,
      webhookUrl: workflow.webhookUrl || '',
      triggerType: workflow.triggerType || N8nTriggerType.WEBHOOK_RECEIVED,
      isActive: workflow.isActive,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      n8nWorkflowId: '',
      webhookUrl: '',
      triggerType: N8nTriggerType.WEBHOOK_RECEIVED,
      isActive: true,
    });
  };

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredWorkflows = workflows.filter(
    (workflow) =>
      workflow.name.toLowerCase().includes(search.toLowerCase()) ||
      workflow.description?.toLowerCase().includes(search.toLowerCase()) ||
      workflow.n8nWorkflowId.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header — hidden when embedded (the parent section provides the title) */}
      <div className="flex items-center justify-between">
        {!embedded && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">n8n Workflows</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage n8n workflow integrations and triggers
            </p>
          </div>
        )}
        <div className={`flex items-center gap-2${embedded ? ' ml-auto' : ''}`}>
          <Button
            variant="outline"
            onClick={async () => {
              await loadAvailableWorkflows();
              setShowImportModal(true);
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Import from n8n
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Workflow
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('workflows')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'workflows'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Workflow className="w-4 h-4 inline mr-2" />
            Workflow Mappings
          </button>
          <button
            onClick={() => setActiveTab('executions')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'executions'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            Execution History
          </button>
          <button
            onClick={() => setActiveTab('connection')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'connection'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Connection
          </button>
        </nav>
      </div>

      {/* Workflows Tab */}
      {activeTab === 'workflows' && (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <Input
              placeholder="Search workflows..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Workflows List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : filteredWorkflows.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <Workflow className="w-12 h-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No n8n workflow mappings
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Add a workflow mapping to trigger n8n workflows from CentraOne
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Workflow
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredWorkflows.map((workflow) => (
                <div
                  key={workflow.id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* Workflow Header */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => toggleExpand(workflow.id, 'workflow')}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          workflow.isActive ? 'bg-green-500' : 'bg-gray-400'
                        }`}
                      />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          {workflow.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          n8n ID: {workflow.n8nWorkflowId}
                          {workflow.triggerType && ` | Trigger: ${TRIGGER_LABELS[workflow.triggerType]}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTriggerWorkflow(workflow);
                        }}
                        className="p-1 text-gray-400 hover:text-green-600 dark:text-gray-500"
                        title="Test Trigger"
                        disabled={triggeringId === workflow.id || !workflow.isActive}
                      >
                        {triggeringId === workflow.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleWorkflow(workflow);
                        }}
                        className={`p-1 ${
                          workflow.isActive
                            ? 'text-green-600 hover:text-green-700'
                            : 'text-gray-400 hover:text-gray-600 dark:text-gray-500'
                        }`}
                        title={workflow.isActive ? 'Disable' : 'Enable'}
                      >
                        {workflow.isActive ? (
                          <Power className="w-4 h-4" />
                        ) : (
                          <PowerOff className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(workflow);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 dark:text-gray-500"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteWorkflow(workflow);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedWorkflows.has(workflow.id) ? (
                        <ChevronUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedWorkflows.has(workflow.id) && (
                    <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700">
                      {workflow.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 mt-3">
                          {workflow.description}
                        </p>
                      )}

                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            n8n Workflow ID
                          </p>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                              {workflow.n8nWorkflowId}
                            </code>
                            <button
                              onClick={() => copyToClipboard(workflow.n8nWorkflowId, `n8n-${workflow.id}`)}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                            >
                              {copiedId === `n8n-${workflow.id}` ? (
                                <Check className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Trigger Type
                          </p>
                          <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                            {workflow.triggerType ? TRIGGER_LABELS[workflow.triggerType] : 'Not Set'}
                          </span>
                        </div>
                      </div>

                      {workflow.webhookUrl && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Webhook URL
                          </p>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded break-all">
                              {workflow.webhookUrl}
                            </code>
                            <button
                              onClick={() => copyToClipboard(workflow.webhookUrl!, `url-${workflow.id}`)}
                              className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0 dark:text-gray-500"
                            >
                              {copiedId === `url-${workflow.id}` ? (
                                <Check className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                        Created: {new Date(workflow.createdAt).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Executions Tab */}
      {activeTab === 'executions' && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Recent n8n workflow executions
            </p>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : executions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <History className="w-12 h-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No executions yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Executions will appear here when workflows are triggered
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Workflow
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Entity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Started
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {executions.map((execution) => (
                    <tr key={execution.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                            STATUS_COLORS[execution.status]
                          }`}
                        >
                          {STATUS_ICONS[execution.status]}
                          {execution.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <code className="text-xs">{execution.workflowId.slice(0, 8)}...</code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {execution.entityType && (
                          <>
                            {execution.entityType}:{' '}
                            <span className="font-mono text-xs">{execution.entityId?.slice(0, 8)}</span>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {execution.startedAt
                          ? new Date(execution.startedAt).toLocaleString()
                          : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {execution.startedAt && execution.completedAt
                          ? `${(
                              (new Date(execution.completedAt).getTime() -
                                new Date(execution.startedAt).getTime()) /
                              1000
                            ).toFixed(2)}s`
                          : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => toggleExpand(execution.id, 'execution')}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Expanded execution details */}
              {executions.filter(e => expandedExecutions.has(e.id)).map(execution => (
                <div
                  key={`detail-${execution.id}`}
                  className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Execution Details: {execution.id.slice(0, 8)}...
                    </h4>
                    <button
                      onClick={() => toggleExpand(execution.id, 'execution')}
                      className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {execution.errorMessage && (
                    <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-300">
                      Error: {execution.errorMessage}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1 dark:text-gray-400">Trigger Payload</p>
                      <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-auto max-h-40">
                        {JSON.stringify(execution.triggerPayload, null, 2)}
                      </pre>
                    </div>
                    {execution.result && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1 dark:text-gray-400">Result</p>
                        <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-auto max-h-40">
                          {JSON.stringify(execution.result, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Connection Tab */}
      {activeTab === 'connection' && (
        <div className="space-y-6">
          {/* Connection Status Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  n8n Connection Status
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Check connectivity to your n8n instance (configured via environment variables)
                </p>
              </div>
              <Button variant="outline" onClick={testConnection} disabled={testingConnection}>
                {testingConnection ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Test Connection
                  </>
                )}
              </Button>
            </div>

            <div
              className={`flex items-center gap-3 p-4 rounded-lg ${
                connectionStatus === 'connected'
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : connectionStatus === 'error'
                  ? 'bg-red-50 dark:bg-red-900/20'
                  : 'bg-gray-50 dark:bg-gray-700'
              }`}
            >
              {connectionStatus === 'connected' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : connectionStatus === 'error' ? (
                <XCircle className="w-5 h-5 text-red-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              )}
              <div>
                <p
                  className={`text-sm font-medium ${
                    connectionStatus === 'connected'
                      ? 'text-green-700 dark:text-green-300'
                      : connectionStatus === 'error'
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {connectionStatus === 'connected'
                    ? 'Connected'
                    : connectionStatus === 'error'
                    ? 'Connection Failed'
                    : 'Not Tested'}
                </p>
                {connectionMessage && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {connectionMessage}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Available Workflows from n8n */}
          {connectionStatus === 'connected' && availableWorkflows.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                Available Workflows in n8n ({availableWorkflows.length})
              </h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {availableWorkflows.map((wf) => (
                  <div
                    key={wf.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          wf.active ? 'bg-green-500' : 'bg-gray-400'
                        }`}
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {wf.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {wf.id} | {wf.active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleImportWorkflow(wf)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Import
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Configuration Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Configuration
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              n8n connection is configured via environment variables:
            </p>
            <ul className="mt-3 text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">N8N_BASE_URL</code> - n8n instance URL</li>
              <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">N8N_API_KEY</code> - API key for authentication</li>
              <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">N8N_WEBHOOK_BASE_URL</code> - Base URL for webhooks</li>
            </ul>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <WorkflowModal
          title={showCreateModal ? 'Add Workflow Mapping' : 'Edit Workflow Mapping'}
          formData={formData}
          setFormData={setFormData}
          onClose={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            resetForm();
          }}
          onSave={showCreateModal ? handleCreateWorkflow : handleUpdateWorkflow}
          saving={saving}
          availableWorkflows={availableWorkflows}
          onLoadAvailable={loadAvailableWorkflows}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          workflows={availableWorkflows}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportWorkflow}
          onRefresh={loadAvailableWorkflows}
        />
      )}
    </div>
  );
}

// Workflow Form Modal
function WorkflowModal({
  title,
  formData,
  setFormData,
  onClose,
  onSave,
  saving,
  availableWorkflows,
  onLoadAvailable,
}: {
  title: string;
  formData: CreateN8nWorkflowDto;
  setFormData: (data: CreateN8nWorkflowDto) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  availableWorkflows: N8nAvailableWorkflow[];
  onLoadAvailable: () => Promise<N8nAvailableWorkflow[]>;
}) {
  const [showTriggerDropdown, setShowTriggerDropdown] = useState(false);
  const [showWorkflowDropdown, setShowWorkflowDropdown] = useState(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);

  const handleLoadWorkflows = async () => {
    setLoadingWorkflows(true);
    await onLoadAvailable();
    setLoadingWorkflows(false);
    setShowWorkflowDropdown(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Email Classifier Workflow"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What does this workflow do?"
              rows={2}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
            />
          </div>

          {/* n8n Workflow ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              n8n Workflow ID *
            </label>
            <div className="flex gap-2">
              <Input
                value={formData.n8nWorkflowId}
                onChange={(e) => setFormData({ ...formData, n8nWorkflowId: e.target.value })}
                placeholder="e.g., email-classifier or workflow ID"
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={handleLoadWorkflows}
                disabled={loadingWorkflows}
              >
                {loadingWorkflows ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </Button>
            </div>
            {showWorkflowDropdown && availableWorkflows.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                {availableWorkflows.map((wf) => (
                  <button
                    key={wf.id}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, n8nWorkflowId: wf.id, name: formData.name || wf.name });
                      setShowWorkflowDropdown(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <span className="font-medium">{wf.name}</span>
                    <span className="text-gray-400 ml-2 dark:text-gray-500">({wf.id})</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Webhook URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Custom Webhook URL (optional)
            </label>
            <Input
              value={formData.webhookUrl}
              onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
              placeholder="https://n8n.example.com/webhook/my-workflow"
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              Leave blank to use default: webhook-base-url/n8n-workflow-id
            </p>
          </div>

          {/* Trigger Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Trigger Type
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTriggerDropdown(!showTriggerDropdown)}
                className="w-full flex items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
              >
                <span>{formData.triggerType ? TRIGGER_LABELS[formData.triggerType] : 'Select trigger type'}</span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showTriggerDropdown ? 'rotate-180' : ''} dark:text-gray-500`} />
              </button>
              {showTriggerDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowTriggerDropdown(false)} />
                  <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, triggerType: value as N8nTriggerType });
                          setShowTriggerDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                          formData.triggerType === value
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              When this event occurs, the workflow will be triggered automatically
            </p>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-700"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
              Active (workflow can be triggered)
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t dark:border-gray-700 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Import Modal
function ImportModal({
  workflows,
  onClose,
  onImport,
  onRefresh,
}: {
  workflows: N8nAvailableWorkflow[];
  onClose: () => void;
  onImport: (workflow: N8nAvailableWorkflow) => void;
  onRefresh: () => Promise<N8nAvailableWorkflow[]>;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };

  const filtered = workflows.filter(
    (wf) =>
      wf.name.toLowerCase().includes(search.toLowerCase()) ||
      wf.id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Import from n8n
          </h3>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:text-gray-500">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 border-b dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <Input
              placeholder="Search workflows..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {workflows.length === 0 ? 'No workflows found in n8n' : 'No matching workflows'}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((wf) => (
                <div
                  key={wf.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                  onClick={() => onImport(wf)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        wf.active ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {wf.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {wf.id} | {wf.active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default N8nSettings;
